import { bootGame, type GameSession } from "@host/boot.js";
import type { CompleteRunResponse, QualityLevel, RunSummary } from "@host/types.js";
import { exportRunBundle, loadRunBundle } from "@host/bundles.js";
import { formatBest, formatSimpleBest, getBest, getSimpleBest } from "@host/scores.js";
import { replayBundle } from "@game/simulation/Simulation.js";
import { CURATED_SEEDS, SIMPLE_SEEDS } from "@game/simulation/seeds.js";
import { FACILITY_CAPACITY, GAME_VERSION, type GameSeed, type RunBundle } from "@game/simulation/types.js";
import { GRADE_BAND_HEX, installShellTokens } from "./tokens.js";
import { ArcadeLedger } from "@host/arcade.js";
import { qualifies } from "@host/HighScoreTable.js";
import { createInitialsEntry, renderScoreTable, type InitialsEntryHandle } from "./arcade-ui.js";

installShellTokens();

type Screen = "boot" | "title" | "shift-select" | "settings" | "playing" | "paused" | "ended";

const stage = el<HTMLElement>("stage");
const canvas = el<HTMLCanvasElement>("game-canvas");
const boot = el("boot");
const bootStatus = el("boot-status");
const bootBar = el("boot-bar");
const title = el("title");
const shiftSelect = el("shift-select");
const settingsPanel = el("settings");
const paused = el("paused");
const ended = el("ended");
const btnPause = el<HTMLButtonElement>("btn-pause");
const abandonConfirm = el("abandon-confirm");
const runNotes = el<HTMLTextAreaElement>("run-notes");
const phaseStart = el<HTMLSelectElement>("phase-start");
const replayStatus = el("replay-status");

const overlays: Record<Exclude<Screen, "playing">, HTMLElement> = {
  boot,
  title,
  "shift-select": shiftSelect,
  settings: settingsPanel,
  paused,
  ended,
};

let session: GameSession | null = null;
let screen: Screen = "boot";
let selectedSeed = CURATED_SEEDS[0]!.id;
let activeSeed = selectedSeed;
let activePhase = 1;
let onboardingDone = false;
let settingsReturn: Screen = "title";
let settingsInvoker: HTMLElement | null = null;
let lastBundle: RunBundle | null = loadRunBundle();
let autoProbeTimer: ReturnType<typeof setTimeout> | null = null;
// The arcade ledger (LOCKED-DESIGN 18.28): shared /api/scores on the deployed
// site, per-browser localStorage everywhere else — the client hides which.
const arcade = new ArcadeLedger();
let initialsWidget: InitialsEntryHandle | null = null;
// Generation tokens for the two async paint paths (review findings): the
// screen VALUE can be "ended" again for a NEWER run while an older load is
// still in flight, and the title attract can resolve out of order across mode
// toggles. A stale generation never paints.
let arcadeGeneration = 0;
let titleScoresGeneration = 0;

const editorPlaytest = import.meta.env.DEV && new URLSearchParams(location.search).has("arenaDraft");

function el<T extends HTMLElement = HTMLElement>(id: string): T {
  const node = document.getElementById(id);
  if (!node) throw new Error(`#${id} missing`);
  return node as T;
}

function show(node: HTMLElement, visible: boolean): void {
  node.classList.toggle("hidden", !visible);
}

function prefersReducedMotion(): boolean {
  return typeof matchMedia !== "undefined" && matchMedia("(prefers-reduced-motion: reduce)").matches;
}

// ---------- play mode (LOCKED-DESIGN 18.11) ----------

/** Current presentation mode; "standard" until the session boots (qa default). */
function currentMode(): "standard" | "simple" {
  return session?.mode ?? "standard";
}

function isSimple(): boolean {
  return currentMode() === "simple";
}

/** The seed roster surfaced for the active mode (kid seeds are unranked). */
function seedsForMode(): readonly GameSeed[] {
  return isSimple() ? SIMPLE_SEEDS : CURATED_SEEDS;
}

/** Onboarding is tracked per mode so adult-done never skips kid orientation. */
function onboardingSlot(): string {
  return isSimple() ? "onboarding-simple" : "onboarding";
}

/** Keep the current selection valid for the active mode's roster. */
function ensureSeedForMode(): void {
  if (!seedsForMode().some((seed) => seed.id === selectedSeed)) {
    selectedSeed = seedsForMode()[0]!.id;
  }
}

/** Reflect the active mode on every segmented mode control. */
function syncModeButtons(): void {
  const mode = currentMode();
  for (const btn of document.querySelectorAll<HTMLButtonElement>(".mode-btn")) {
    btn.classList.toggle("selected", btn.dataset.mode === mode);
    btn.setAttribute("aria-pressed", String(btn.dataset.mode === mode));
  }
}

/** Select + persist a play mode, then re-render whatever screen is showing. */
function selectMode(mode: "standard" | "simple"): void {
  if (!session || currentMode() === mode) return;
  session.setMode(mode);
  ensureSeedForMode();
  syncModeButtons();
  // The Simple-only settings group is mode-scoped (18.14 / F4), so re-render it
  // whenever the mode changes on the title — otherwise a player who switches to
  // Standard and opens Settings without a reload still sees the dead control.
  syncSettingsUi();
  if (screen === "title") void enterTitle();
  else if (screen === "shift-select") renderShiftSelect();
}

// ---------- screen / focus management ----------

function activeOverlay(): HTMLElement | null {
  return screen === "playing" || screen === "boot" ? null : overlays[screen];
}

/**
 * The element that currently owns modal focus. When the abandon-confirm
 * sub-dialog is open it traps focus within itself (not the whole pause panel),
 * matching the Phase-3 dialog focus pattern (accessibility 12.3).
 */
function activeDialogScope(): HTMLElement | null {
  if (screen === "paused" && !abandonConfirm.classList.contains("hidden")) return abandonConfirm;
  return activeOverlay();
}

function focusDialog(node: HTMLElement): void {
  const target =
    node.querySelector<HTMLElement>("[data-autofocus]") ??
    node.querySelector<HTMLElement>("button.primary") ??
    node.querySelector<HTMLElement>("button, input, select, textarea, [tabindex]");
  target?.focus();
}

function setScreen(next: Screen): void {
  screen = next;
  for (const [name, node] of Object.entries(overlays)) {
    show(node, name === next);
  }
  show(btnPause, next === "playing");
  // Hide the in-arena sim HUD whenever a shell overlay / non-playing state is up
  // (LOCKED-DESIGN 18.10 residual): the RAF loop is stopped before a run, so the
  // idle chrome must not bleed through the title/settings overlays.
  session?.setHudVisible(next === "playing");
  hideAbandonConfirm();
  // A pending initials selector dies with the results screen — its gamepad
  // poll and key listener must never outlive the overlay that owns it, and its
  // ELEMENT must leave the DOM: it carries the app's only [data-autofocus],
  // and a stale one inside #arcade-panel would hijack the NEXT run's
  // focusDialog and then be replaced out from under the focus (review finding).
  if (next !== "ended" && initialsWidget) {
    initialsWidget.dispose();
    initialsWidget.element.remove();
    initialsWidget = null;
  }
  if (next === "playing") {
    // Return keyboard focus to the stage on resume; never steal it while playing.
    stage.focus();
  } else if (next !== "boot") {
    focusDialog(overlays[next]);
  }
}

/** Basic focus trap so Tab cycles within the open dialog (accessibility 12.3). */
function trapTab(event: KeyboardEvent): void {
  if (event.key !== "Tab") return;
  const overlay = activeDialogScope();
  if (!overlay) return;
  const focusables = [...overlay.querySelectorAll<HTMLElement>(
    'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
  )].filter((node) => node.offsetParent !== null);
  if (focusables.length === 0) return;
  const first = focusables[0]!;
  const last = focusables[focusables.length - 1]!;
  const current = document.activeElement as HTMLElement | null;
  if (event.shiftKey && current === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && current === last) {
    event.preventDefault();
    first.focus();
  } else if (current && !overlay.contains(current)) {
    event.preventDefault();
    first.focus();
  }
}

// ---------- best labels ----------

function refreshBestLabels(): void {
  // Simple reads its OWN best slot (LOCKED-DESIGN 18.11) so kid scores never
  // clobber the ranked labels.
  const text = isSimple() ? formatSimpleBest(getSimpleBest()) : formatBest(getBest());
  el("title-best").textContent = text;
  el("shift-best").textContent = text;
  renderTitleScores();
}

// ---------- seed cards ----------

function renderSeedCards(): void {
  const grid = el("seed-grid");
  grid.replaceChildren(
    ...seedsForMode().map((seed) => {
      const button = document.createElement("button");
      button.type = "button";
      button.className = `seed-card${seed.id === selectedSeed ? " selected" : ""}`;
      button.dataset.testid = `seed-${seed.id}`;
      const strong = document.createElement("strong");
      strong.textContent = seed.title;
      const span = document.createElement("span");
      span.textContent = seed.description;
      button.append(strong, span);
      button.addEventListener("click", () => {
        selectedSeed = seed.id;
        renderSeedCards();
      });
      return button;
    }),
  );
}

/**
 * Render the shift-select screen for the active mode (LOCKED-DESIGN 18.11).
 * Simple shows only the two kid seeds as big cards, hides the dev phase-start /
 * keybind affordances, and swaps to early-reader copy; NORMAL is today's screen.
 */
function renderShiftSelect(): void {
  const simple = isSimple();
  ensureSeedForMode();
  (shiftSelect.querySelector(".panel") as HTMLElement).classList.toggle("is-simple", simple);
  el("shift-heading").textContent = simple ? "PICK A SHIFT" : "Select shift";
  el("btn-shift-back").textContent = simple ? "◂ BACK" : "◂ Title";
  el("btn-play-prologue").textContent = simple ? "PLAY INTRO ▸" : "Orientation";
  el("btn-start-shift").textContent = simple ? "GO! ▸" : "Start shift ▸";
  renderSeedCards();
  refreshBestLabels();
  syncModeButtons();
}

// ---------- run lifecycle ----------

function beginShift(): void {
  if (!session) return;
  activeSeed = selectedSeed;
  // Simple ignores the dev Practice-start select (LOCKED-DESIGN 18.11): it is
  // only display:none for kids, so its value would otherwise persist across a
  // NORMAL->SIMPLE toggle and silently open a kid's run at peak phase-5 pressure.
  activePhase = isSimple() ? 1 : Number(phaseStart.value);
  session.configureRun(activeSeed, activePhase);
  session.start();
  setScreen("playing");
  scheduleAutoProbe();
}

function beginPrologue(): void {
  if (!session) return;
  // Retrying an orientation-graduated run should launch a real shift.
  activeSeed = selectedSeed;
  // Simple pins phase 1 regardless of the hidden Practice-start select (18.11).
  activePhase = isSimple() ? 1 : Number(phaseStart.value);
  // Pin the seed the orientation graduates INTO before arming the prologue, so
  // Simple runs the kid seed (not the default ranked seed) — configureRun sets
  // ctx.seed, startPrologue then re-arms the tutorial (LOCKED-DESIGN 18.11). For
  // adult this is a no-op: selectedSeed defaults to the same first ranked seed.
  session.configureRun(activeSeed, activePhase);
  session.startPrologue();
  session.start();
  setScreen("playing");
  scheduleAutoProbe();
}

function retryShift(): void {
  if (!session) return;
  session.configureRun(activeSeed, activePhase);
  session.start();
  setScreen("playing");
  scheduleAutoProbe();
}

function pauseRun(): void {
  if (!session || screen !== "playing") return;
  session.pause();
  renderPauseContext();
  setScreen("paused");
}

function resumeRun(): void {
  if (!session || screen !== "paused") return;
  session.resume();
  setScreen("playing");
}

function restartShift(): void {
  if (!session) return;
  // Restarting from the pause menu DURING orientation restarts orientation —
  // it never silently graduates the player into a scored shift (18.6).
  if (session.isTutorialActive()) {
    session.startPrologue();
    session.start();
    setScreen("playing");
    return;
  }
  session.configureRun(activeSeed, activePhase);
  session.start();
  setScreen("playing");
  scheduleAutoProbe();
}

async function enterTitle(): Promise<void> {
  ensureSeedForMode();
  syncModeButtons();
  refreshBestLabels();
  onboardingDone = await isOnboardingDone();
  const beginBtn = el("title-begin");
  // Simple CTA speaks the early-reader voice (LOCKED-DESIGN 18.11); NORMAL keeps
  // the adult copy so the qa:arena title-begin path is unchanged.
  beginBtn.textContent = isSimple()
    ? onboardingDone ? "PICK A SHIFT ▸" : "LET'S PLAY! ▸"
    : onboardingDone ? "Select shift ▸" : "Start orientation ▸";
  setScreen("title");
}

async function isOnboardingDone(): Promise<boolean> {
  if (!session) return false;
  try {
    const saved = await session.context.load(onboardingSlot());
    return saved?.["done"] === true;
  } catch {
    return false;
  }
}

// ---------- pause context ----------

function renderPauseContext(): void {
  const status = session?.getLiveStatus();
  const inTutorial = session?.isTutorialActive() ?? false;
  const simple = isSimple();
  (paused.querySelector(".panel") as HTMLElement).classList.toggle("is-simple", simple);
  // Simple pause (LOCKED-DESIGN 18.11): warm heading, score-only context, kid
  // button copy; HOME goes straight to the title (abandon-confirm is hidden).
  if (simple) {
    el("pause-heading").textContent = "PAUSED";
    el("btn-resume").textContent = "KEEP PLAYING ▸";
    el("btn-restart").textContent = "START OVER";
    el("btn-pause-settings").textContent = "SOUND & SETTINGS";
    el("btn-pause-title").textContent = "HOME";
  } else {
    el("btn-resume").textContent = "Resume";
    el("btn-restart").textContent = "Restart shift";
    el("btn-pause-settings").textContent = "Settings";
    el("btn-pause-title").textContent = "Title";
  }
  const seedTitle = seedsForMode().find((s) => s.id === activeSeed)?.title
    ?? CURATED_SEEDS.find((s) => s.id === activeSeed)?.title
    ?? "Shift";
  if (!simple) el("pause-heading").textContent = inTutorial ? "Orientation paused" : `${seedTitle} — paused`;
  const items: Array<[string, string]> = inTutorial
    ? [["Orientation", simple ? "Warm-up" : "Interactive warm-up"]]
    : simple
      ? [[status ? status.score.toLocaleString() : "—", "Score"]]
      : [
          [status ? status.score.toLocaleString() : "—", "Score so far"],
          [status ? `${status.phaseIndex}/6` : "—", "Phase"],
          [status ? `${Math.round(status.overflow)}/${FACILITY_CAPACITY}` : "—", "Overflow"],
          [status ? `${Math.round(status.grade)}` : "—", "Grade"],
        ];
  el("pause-context").replaceChildren(
    ...items.map(([value, label]) => {
      const item = document.createElement("div");
      item.className = "context-item";
      const strong = document.createElement("strong");
      strong.textContent = value;
      const span = document.createElement("span");
      span.textContent = label;
      item.append(strong, span);
      return item;
    }),
  );
}

function showAbandonConfirm(): void {
  show(abandonConfirm, true);
  el("btn-abandon-confirm").focus();
}

function hideAbandonConfirm(): void {
  show(abandonConfirm, false);
}

/**
 * Dismiss the abandon-confirm sub-dialog and return focus to the control that
 * opened it (LOCKED-DESIGN 18.10 residual). Used by Keep-going and Escape; the
 * raw hideAbandonConfirm() stays focus-neutral for screen transitions.
 */
function cancelAbandonConfirm(): void {
  const wasOpen = !abandonConfirm.classList.contains("hidden");
  hideAbandonConfirm();
  if (wasOpen) el("btn-abandon").focus();
}

function abandonShift(): void {
  session?.context.reportTelemetry("shift_abandoned", { seed: activeSeed, phase: activePhase });
  void enterTitle();
}

// ---------- settings ----------

function openSettings(from: Screen): void {
  settingsReturn = from;
  // Remember the control that opened settings so closing returns focus to it
  // (LOCKED-DESIGN 18.10 residual: overlay close must restore the invoker).
  settingsInvoker = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  syncSettingsUi();
  setScreen("settings");
}

function closeSettings(): void {
  const invoker = settingsInvoker;
  settingsInvoker = null;
  setScreen(settingsReturn);
  if (settingsReturn === "paused") renderPauseContext();
  // setScreen focuses the return dialog's default control; override that with
  // the exact control that opened settings when it is still visible.
  if (invoker && invoker.isConnected && invoker.offsetParent !== null) invoker.focus();
}

function syncSettingsUi(): void {
  if (!session) return;
  const s = session.settings;
  (el<HTMLInputElement>("set-master")).value = String(Math.round(s.masterVolume * 100));
  (el<HTMLInputElement>("set-music")).value = String(Math.round(s.musicVolume * 100));
  (el<HTMLInputElement>("set-sfx")).value = String(Math.round(s.sfxVolume * 100));
  (el<HTMLInputElement>("set-mute")).checked = s.muted;
  (el<HTMLInputElement>("set-shake")).value = String(Math.round(s.screenshakeIntensity * 100));
  (el<HTMLInputElement>("set-flash")).checked = s.flashReduction;
  // Simple-only group (LOCKED-DESIGN 18.14 / F4): the toggle only applies to
  // Simple runs (the kernel refuses it on ranked seeds), so the whole group is
  // hidden in Standard rather than shown as a dead control. The checkbox still
  // reflects the persisted value so switching back to Simple restores it.
  (el<HTMLInputElement>("set-jams")).checked = s.simpleJamsDisabled;
  show(el("settings-simple"), isSimple());
  (el<HTMLSelectElement>("quality-select")).value = s.qualityAuto ? "auto" : s.quality;
  updateQualityNote();
}

function updateQualityNote(): void {
  if (!session) return;
  const s = session.settings;
  const note = el("quality-note");
  if (s.qualityAuto) {
    note.textContent = `Auto — calibrating from live fps. Current tier: ${s.quality}.`;
  } else {
    note.textContent = "Higher tiers add bloom, soft shadows, and anti-aliasing. Lower tiers keep 60 fps on weaker GPUs.";
  }
}

/** fps-probe auto-detect (LOCKED-DESIGN 18.5): pick a tier from live fps. */
function scheduleAutoProbe(): void {
  if (autoProbeTimer) clearTimeout(autoProbeTimer);
  if (!session || !session.settings.qualityAuto) return;
  autoProbeTimer = setTimeout(() => {
    if (!session || screen !== "playing" || !session.settings.qualityAuto) return;
    const fps = session.getFps();
    if (fps <= 0) return;
    const tier: QualityLevel = fps >= 55 ? "high" : fps >= 40 ? "medium" : "low";
    if (tier !== session.settings.quality) session.setQuality(tier, true);
  }, 2600);
}

// ---------- results ----------

function onRunEnd(summary: RunSummary, result: CompleteRunResponse): void {
  if (autoProbeTimer) clearTimeout(autoProbeTimer);
  session?.pause();
  lastBundle = loadRunBundle() ?? session?.getRunBundle() ?? null;

  const score = summary.metrics.find((m) => m.leaderboardId === "score")?.value ?? 0;

  if (isSimple()) {
    renderSimpleResults(summary, score);
    return;
  }

  const grade = Number(summary.metadata?.["grade"] ?? 0);
  const band = bandOf(grade);
  const isFailure = summary.endReason === "death" || summary.endReason === "abandon" || score <= 0;
  const isWin = summary.endReason === "cash-out" && score > 0;

  const panel = el("ended").querySelector<HTMLElement>(".results-panel")!;
  panel.classList.remove("is-simple");
  const hero = el("ended").querySelector<HTMLElement>(".results-hero")!;
  hero.classList.toggle("is-failure", isFailure);
  hero.classList.toggle("is-win", isWin && !isFailure);
  // reset any Simple chrome the previous run may have set
  (hero.querySelector(".score-label") as HTMLElement).textContent = "Score";
  el("end-grade").style.display = "";
  el("end-sub").textContent = "";
  el("btn-retry").textContent = "Retry shift ▸";
  el("btn-change-shift").textContent = "Change shift";
  el("btn-title").textContent = "Title";

  el("end-reason").textContent = reasonLabel(summary);

  // Grade band letter (Section 8.3), not the raw number.
  const bandEl = el("end-grade-band");
  bandEl.textContent = band;
  const gradeBadge = el("end-grade");
  gradeBadge.style.setProperty("--grade-color", GRADE_BAND_HEX[band]);
  gradeBadge.style.setProperty("--grade-glow", isFailure ? "transparent" : `${GRADE_BAND_HEX[band]}55`);
  el("end-grade-val").textContent = String(Math.round(grade));

  // Animated score count-up (snaps under prefers-reduced-motion / zero score).
  animateCount(el("end-score"), score);

  // Best line — celebratory only when scores.ts confirmed a real best.
  const bestEl = el("end-best");
  if (result.message) {
    bestEl.textContent = result.message;
    bestEl.style.color = "var(--tpz-teal)";
  } else {
    bestEl.textContent = formatBest(getBest());
    bestEl.style.color = "";
  }

  renderAnalysis(summary);

  replayStatus.textContent = "";
  replayStatus.style.color = "";
  runNotes.value = lastBundle?.notes ?? "";
  // Collapse drawers each run so results open arcade-first.
  (el<HTMLDetailsElement>("end-analysis")).open = false;
  (el<HTMLDetailsElement>("end-diagnostics")).open = false;

  refreshBestLabels();
  setScreen("ended");
  void presentArcade(score);
}

/**
 * Simple results (LOCKED-DESIGN 18.11): hero only — no grade badge, no analysis
 * or diagnostics drawers, no failure framing. Big score under "YOU SCORED", a
 * warm reason + encouraging sub-line, the kid best, and PLAY AGAIN primary.
 */
function renderSimpleResults(summary: RunSummary, score: number): void {
  const panel = el("ended").querySelector<HTMLElement>(".results-panel")!;
  panel.classList.add("is-simple");
  const hero = el("ended").querySelector<HTMLElement>(".results-hero")!;
  // The hero always reads warm in Simple — never the failure/win styling.
  hero.classList.remove("is-failure");
  hero.classList.remove("is-win");

  const complete = summary.endReason === "cash-out";
  const abandon = summary.endReason === "abandon";
  const reason = complete ? "SHIFT DONE!" : abandon ? "SEE YOU SOON!" : "GREAT SHIFT!";
  // Warm, never failure-framed (LOCKED-DESIGN 18.12, resolved question B): the two
  // new jam-recovery ends get their own soft kid line; overflow keeps its line.
  const simEnd = summary.metadata?.["simEndReason"];
  const failSub = simEnd === "cooked"
    ? "It got too hot! Try again!"
    : simEnd === "jam-overload"
      ? "The furnace got too full — try again!"
      : "The bins got full — try again!";
  const sub = complete ? "You did it!" : abandon ? "" : failSub;
  el("end-reason").textContent = reason;
  el("end-sub").textContent = sub;

  (hero.querySelector(".score-label") as HTMLElement).textContent = "YOU SCORED";
  el("end-grade").style.display = "none";
  animateCount(el("end-score"), score);

  const bestEl = el("end-best");
  bestEl.textContent = formatSimpleBest(getSimpleBest());
  bestEl.style.color = "var(--tpz-teal)";

  el("btn-retry").textContent = "PLAY AGAIN ▸";
  el("btn-change-shift").textContent = "PICK A SHIFT";
  el("btn-title").textContent = "HOME";

  // Collapse (and hide via .is-simple CSS) the adult drawers each run.
  (el<HTMLDetailsElement>("end-analysis")).open = false;
  (el<HTMLDetailsElement>("end-diagnostics")).open = false;

  refreshBestLabels();
  setScreen("ended");
  void presentArcade(score);
  if (complete) launchConfetti();
}

/**
 * The arcade panel on the results screen (LOCKED-DESIGN 18.28). Runs AFTER
 * setScreen("ended") so the QA pin — end-reason visible immediately at the
 * buzzer — is untouchable by construction; everything here fills in async
 * beneath the hero. Every await re-checks the screen so a player who already
 * hit PLAY AGAIN never gets a stale table painted under a new run.
 */
async function presentArcade(score: number): Promise<void> {
  const generation = ++arcadeGeneration;
  // Not just `screen === "ended"`: run 2 can END while run 1's load is still
  // in flight, making the screen value "ended" again for a DIFFERENT run —
  // the generation token is what makes staleness detectable (review finding).
  const live = () => screen === "ended" && generation === arcadeGeneration;
  const container = el("arcade-panel");
  container.replaceChildren();
  const simple = isSimple();
  const seedId = activeSeed;
  const title = [...CURATED_SEEDS, ...SIMPLE_SEEDS].find((seed) => seed.id === seedId)?.title ?? seedId;
  const heading = `${title.toUpperCase()} · HIGH SCORES`;

  // ONE ledger load serves both the qualify check and the no-prompt table —
  // the shouldPrompt helper's internal load was a second identical round trip
  // that doubled the panel's worst-case latency to ~6 s (review finding).
  const initialView = await arcade.load(seedId);
  if (!live()) return;
  // Practice starts (phase > 1) play a shorter shift — they see the table but
  // never enter it, or a phase-6 sprint would wall the board off from full runs.
  const eligible = activePhase === 1;
  const prompt = eligible && qualifies(initialView.entries, score);

  type View = { entries: import("@host/HighScoreTable.js").HighScoreEntry[]; source: "shared" | "local" };
  const showTable = async (highlight: { initials: string; score: number; ts: number } | null, rank: number | null, presetView?: View) => {
    const view = presetView ?? await arcade.load(seedId);
    if (!live()) return;
    container.replaceChildren();
    if (rank !== null) {
      const banner = document.createElement("p");
      banner.className = "arcade-rank-banner";
      banner.dataset["testid"] = "arcade-rank";
      banner.textContent = simple ? `YOU ARE #${rank}!` : `New high score — rank #${rank}`;
      container.append(banner);
    }
    const tableBox = document.createElement("div");
    container.append(tableBox);
    renderScoreTable(tableBox, view.entries, { source: view.source, simple, highlight, heading });
  };

  if (!prompt) {
    await showTable(null, null, initialView);
    return;
  }

  initialsWidget = createInitialsEntry({
    initials: arcade.lastInitials(),
    simple,
    onDone: (initials) => {
      initialsWidget = null;
      void (async () => {
        const result = await arcade.submit(seedId, initials, score);
        if (!live()) return;
        await showTable(result.entry, result.rank, { entries: result.entries, source: result.source });
      })();
    },
    onSkip: () => {
      initialsWidget = null;
      void showTable(null, null);
    },
  });
  container.append(initialsWidget.element);
  // Focus the confirm so Enter/Ⓐ locks in the remembered letters instantly.
  initialsWidget.element.querySelector<HTMLElement>("[data-autofocus]")?.focus();
}

/** Attract table on the title screen for the shift the begin button would start. */
function renderTitleScores(): void {
  const container = el("title-scores");
  const seed = seedsForMode().find((candidate) => candidate.id === selectedSeed) ?? seedsForMode()[0]!;
  // Sequence token: mode toggles fire overlapping loads for different seeds,
  // and the 3 s-timeout fallback path means a SLOWER first request can resolve
  // last and paint the wrong shift's board over the right one (review
  // finding). Only the newest request may paint.
  const generation = ++titleScoresGeneration;
  void (async () => {
    const view = await arcade.load(seed.id);
    if (generation !== titleScoresGeneration) return;
    if (screen !== "title" && screen !== "boot" && screen !== "shift-select") return;
    renderScoreTable(container, view.entries, {
      source: view.source,
      simple: isSimple(),
      heading: `${seed.title.toUpperCase()} · HIGH SCORES`,
      limit: 5,
    });
  })();
}

/**
 * Full-screen kid confetti for a completed Simple shift (LOCKED-DESIGN 18.11).
 * Respects prefers-reduced-motion and the flash-reduction knob (12.3): either
 * suppresses the animation entirely. Token-colored; self-cleaning.
 */
function launchConfetti(): void {
  if (typeof document === "undefined") return;
  if (prefersReducedMotion() || session?.settings.flashReduction) return;
  const layer = document.createElement("div");
  layer.className = "confetti-layer";
  const colors = ["--tpz-teal", "--tpz-amber", "--tpz-blue", "--tpz-safe", "--tpz-orange"];
  for (let i = 0; i < 44; i++) {
    const piece = document.createElement("i");
    piece.className = "confetti-piece";
    piece.style.left = `${Math.random() * 100}%`;
    piece.style.background = `var(${colors[i % colors.length]})`;
    piece.style.animationDelay = `${Math.random() * 0.6}s`;
    piece.style.animationDuration = `${1.8 + Math.random() * 1.2}s`;
    piece.style.transform = `rotate(${Math.random() * 360}deg)`;
    layer.append(piece);
  }
  document.body.append(layer);
  setTimeout(() => layer.remove(), 3200);
}

function reasonLabel(summary: RunSummary): string {
  switch (summary.endReason) {
    case "cash-out":
      return "Shift complete";
    case "death": {
      const t = fmtDuration(summary.durationMs);
      // Precise sim end reason (LOCKED-DESIGN 18.12): the two jam-recovery failures
      // name themselves; overflow keeps its line. bundle.final.reason feeds the
      // diagnostics drawer; this reads the shell metadata copy.
      switch (summary.metadata?.["simEndReason"]) {
        case "cooked":
          return `Cooked in the chamber · failed at ${t}`;
        case "jam-overload":
          return `Furnace overload — jam left unattended · failed at ${t}`;
        default:
          return `Critical overflow · failed at ${t}`;
      }
    }
    case "abandon":
      return "Shift abandoned";
    default:
      return "Run over";
  }
}

function renderAnalysis(summary: RunSummary): void {
  const body = el("analysis-body");
  const metrics = lastBundle?.metrics;
  const loadSubtotal = Number(summary.scoreComponents?.["loads"] ?? 0);
  const bestChain = summary.bestChain ?? summary.metrics.find((m) => m.leaderboardId === "combo")?.value ?? 0;
  const incinerated = Number(summary.metadata?.["incinerated"] ?? 0);
  const ticketsCompleted = metrics?.ticketCompleted ?? 0;
  const ticketsExpired = metrics?.ticketExpired ?? 0;
  const ticketTotal = ticketsCompleted + ticketsExpired;

  body.replaceChildren();
  body.append(
    section("Load outcomes", [
      item(String(metrics?.loads.clean ?? 0), "Clean", "pos"),
      item(String(metrics?.loads.partial ?? 0), "Partial"),
      item(String(metrics?.loads.missed ?? 0), "Missed", (metrics?.loads.missed ?? 0) > 0 ? "neg" : undefined),
    ]),
    section("Dispatch tickets", [
      item(`${ticketsCompleted}/${ticketTotal || 6}`, "Completed", ticketsCompleted > 0 ? "pos" : undefined),
      item(String(ticketsExpired), "Expired", ticketsExpired > 0 ? "neg" : undefined),
    ]),
    section("Route usage", routeItems(metrics?.routeUsage)),
    section("Overflow & hazards", [
      item(`${metrics?.overflowPeak ?? 0}/${FACILITY_CAPACITY}`, "Overflow peak", (metrics?.overflowPeak ?? 0) >= 30 ? "neg" : undefined),
      item(String(metrics?.redDetonations ?? 0), "Red blasts", (metrics?.redDetonations ?? 0) > 0 ? "neg" : undefined),
      item(String(metrics?.jams ?? 0), "Jams"),
      // Zero-point bulk hand-fed to the maw clearing jams (LOCKED-DESIGN 18.12).
      item(String(metrics?.jamBulkFed ?? 0), "Recovered", (metrics?.jamBulkFed ?? 0) > 0 ? "pos" : undefined),
      item(String(metrics?.emergencyPurges ?? 0), "Purges", (metrics?.emergencyPurges ?? 0) > 0 ? "neg" : undefined),
    ]),
    section("Scoring", [
      item(loadSubtotal.toLocaleString(), "Load points"),
      item(`×${bestChain}`, "Best chain"),
      item(String(incinerated), "Processed"),
    ]),
    section("Replay", [
      itemWide(lastBundle?.final.ledgerHash ?? "—", "Ledger hash"),
      item(lastBundle?.gameVersion ?? "—", "Version"),
    ]),
  );
}

function routeItems(routeUsage: Record<string, number> | undefined): HTMLElement[] {
  const entries = Object.entries(routeUsage ?? {}).sort((a, b) => b[1] - a[1]).slice(0, 4);
  if (entries.length === 0) return [item("—", "No routes")];
  return entries.map(([route, count]) => item(String(count), route));
}

function section(heading: string, items: HTMLElement[]): HTMLElement {
  const wrap = document.createElement("div");
  wrap.className = "analysis-section";
  const h = document.createElement("h3");
  h.textContent = heading;
  const grid = document.createElement("div");
  grid.className = "analysis-grid";
  grid.append(...items);
  wrap.append(h, grid);
  return wrap;
}

function item(value: string, label: string, tone?: string): HTMLElement {
  const node = document.createElement("div");
  node.className = `analysis-item${tone ? ` ${tone}` : ""}`;
  const strong = document.createElement("strong");
  strong.textContent = value;
  const span = document.createElement("span");
  span.textContent = label;
  node.append(strong, span);
  return node;
}

function itemWide(value: string, label: string): HTMLElement {
  const node = item(value, label);
  node.classList.add("wide");
  return node;
}

function animateCount(node: HTMLElement, to: number): void {
  if (prefersReducedMotion() || to <= 0) {
    node.textContent = Math.max(0, to).toLocaleString();
    return;
  }
  const duration = 900;
  const start = performance.now();
  const step = (now: number): void => {
    const t = Math.min(1, (now - start) / duration);
    const eased = 1 - Math.pow(1 - t, 3);
    node.textContent = Math.round(to * eased).toLocaleString();
    if (t < 1) requestAnimationFrame(step);
    else node.textContent = to.toLocaleString();
  };
  requestAnimationFrame(step);
}

function bandOf(grade: number): "C" | "B" | "A" | "S" {
  return grade >= 85 ? "S" : grade >= 65 ? "A" : grade >= 40 ? "B" : "C";
}

function fmtDuration(ms: number): string {
  const total = Math.round(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

// ---------- diagnostics ----------

function verifyReplay(): void {
  if (!lastBundle) {
    replayStatus.textContent = "No run bundle available.";
    replayStatus.style.color = "var(--tpz-text-dim)";
    return;
  }
  // A bundle recorded on an older kernel is not a determinism failure — the
  // rules it was recorded under no longer exist in this build. Say that
  // plainly instead of accusing the bundle (LOCKED-DESIGN 18.14: the m1.3.0
  // family bundles stop replaying at the m1.4.0 scoring-roll fix).
  if (lastBundle.gameVersion !== GAME_VERSION) {
    replayStatus.textContent = `Recorded on ${lastBundle.gameVersion} — replay unavailable on ${GAME_VERSION}`;
    replayStatus.style.color = "var(--tpz-text-dim)";
    return;
  }
  const replayed = replayBundle(lastBundle);
  const exact =
    replayed.final.ledgerHash === lastBundle.final.ledgerHash &&
    replayed.final.score === lastBundle.final.score;
  replayStatus.textContent = exact
    ? `Replay verified · ${replayed.final.ledgerHash}`
    : "Replay mismatch — bundle quarantined locally.";
  replayStatus.style.color = exact ? "var(--tpz-teal)" : "var(--tpz-red)";
}

// ---------- boot ----------

async function init(): Promise<void> {
  renderSeedCards();
  refreshBestLabels();
  try {
    session = await bootGame(stage, canvas, {
      onProgress: (label, ratio) => {
        bootStatus.textContent = label ? `Loading ${label}…` : "Loading…";
        bootBar.style.width = `${Math.max(8, Math.round(ratio * 100))}%`;
      },
      onRunEnd,
      onTelemetry: (event) => {
        if (import.meta.env.DEV) console.debug("[tpz]", event);
      },
    });
    bootBar.style.width = "100%";
    bootStatus.textContent = "Ready";

    if (editorPlaytest) {
      const back = document.createElement("button");
      back.type = "button";
      back.className = "editor-return";
      back.dataset.testid = "editor-return";
      back.textContent = "◂ Return to editor";
      back.addEventListener("click", () => {
        location.href = "/editor";
      });
      document.body.append(back);
      // Draft playtest jumps straight into a run; audio is primed by this gesture chain.
      session.primeAudio();
      session.configureRun(selectedSeed, 1);
      session.start();
      setScreen("playing");
    } else {
      await enterTitle();
    }
  } catch (err) {
    bootStatus.textContent = err instanceof Error ? `Failed: ${err.message}` : "Failed to load";
    console.error(err);
  }
}

// ---------- wiring ----------

// Play-mode toggles (title + shift header) — persist immediately (18.11).
for (const btn of document.querySelectorAll<HTMLButtonElement>(".mode-btn")) {
  btn.addEventListener("click", () => {
    session?.primeAudio();
    selectMode(btn.dataset.mode === "simple" ? "simple" : "standard");
  });
}

// Title
el("title-begin").addEventListener("click", () => {
  session?.primeAudio();
  if (onboardingDone) {
    renderShiftSelect();
    setScreen("shift-select");
  } else {
    beginPrologue();
  }
});
el("title-shift").addEventListener("click", () => {
  session?.primeAudio();
  renderShiftSelect();
  setScreen("shift-select");
});
el("title-settings").addEventListener("click", () => {
  session?.primeAudio();
  openSettings("title");
});

// Shift select
el("btn-start-shift").addEventListener("click", beginShift);
el("btn-play-prologue").addEventListener("click", beginPrologue);
el("btn-shift-back").addEventListener("click", () => void enterTitle());

// Settings
el("btn-settings-back").addEventListener("click", closeSettings);
el<HTMLInputElement>("set-master").addEventListener("input", (e) => session?.setMasterVolume(Number((e.target as HTMLInputElement).value) / 100));
el<HTMLInputElement>("set-music").addEventListener("input", (e) => session?.setMusicVolume(Number((e.target as HTMLInputElement).value) / 100));
el<HTMLInputElement>("set-sfx").addEventListener("input", (e) => session?.setSfxVolume(Number((e.target as HTMLInputElement).value) / 100));
el<HTMLInputElement>("set-mute").addEventListener("change", (e) => session?.setMuted((e.target as HTMLInputElement).checked));
el<HTMLInputElement>("set-shake").addEventListener("input", (e) => session?.setScreenshakeIntensity(Number((e.target as HTMLInputElement).value) / 100));
el<HTMLInputElement>("set-flash").addEventListener("change", (e) => session?.setFlashReduction((e.target as HTMLInputElement).checked));
// Simple-only jam toggle (LOCKED-DESIGN 18.14 / F4). Persists immediately; the
// Engine reads it once at run creation, so it applies to the NEXT shift.
el<HTMLInputElement>("set-jams").addEventListener("change", (e) => session?.setJamsDisabled((e.target as HTMLInputElement).checked));
el<HTMLSelectElement>("quality-select").addEventListener("change", (e) => {
  const value = (e.target as HTMLSelectElement).value;
  if (!session) return;
  if (value === "auto") {
    session.setQuality(session.settings.quality, true);
    scheduleAutoProbe();
  } else {
    session.setQuality(value as QualityLevel, false);
  }
  updateQualityNote();
});

// Pause
btnPause.addEventListener("click", pauseRun);
el("btn-resume").addEventListener("click", resumeRun);
el("btn-restart").addEventListener("click", restartShift);
el("btn-pause-settings").addEventListener("click", () => openSettings("paused"));
// Simple HOME goes direct to the title (no abandon-confirm — unranked, nothing
// to protect) but STILL fires the abandon telemetry (LOCKED-DESIGN 18.11).
el("btn-pause-title").addEventListener("click", () => {
  if (isSimple()) abandonShift();
  else void enterTitle();
});
el("btn-abandon").addEventListener("click", showAbandonConfirm);
el("btn-abandon-cancel").addEventListener("click", cancelAbandonConfirm);
el("btn-abandon-confirm").addEventListener("click", abandonShift);

// Results
el("btn-retry").addEventListener("click", retryShift);
el("btn-change-shift").addEventListener("click", () => {
  renderShiftSelect();
  setScreen("shift-select");
});
el("btn-title").addEventListener("click", () => void enterTitle());
el("btn-export").addEventListener("click", () => {
  if (lastBundle) exportRunBundle(lastBundle);
});
// Simple-only "Save session" (LOCKED-DESIGN 18.13): same export path as the
// diagnostics drawer's Export bundle; quietly does nothing without a bundle.
el("btn-save-session").addEventListener("click", () => {
  if (lastBundle) exportRunBundle(lastBundle);
});
el("btn-replay").addEventListener("click", verifyReplay);
runNotes.addEventListener("input", () => {
  if (lastBundle) lastBundle.notes = runNotes.value.trim();
});

// Global keys
window.addEventListener("keydown", (event) => {
  if (event.key === "Tab") {
    trapTab(event);
    return;
  }
  if (event.code !== "Escape") return;
  if (screen === "playing") {
    // During the prologue, Escape SKIPS the tutorial (the input system consumes
    // it) — never open the pause menu (LOCKED-DESIGN 18.6 Escape skip fix).
    if (session?.isTutorialActive()) return;
    event.preventDefault();
    pauseRun();
  } else if (screen === "paused") {
    event.preventDefault();
    if (!abandonConfirm.classList.contains("hidden")) cancelAbandonConfirm();
    else resumeRun();
  } else if (screen === "settings") {
    event.preventDefault();
    closeSettings();
  }
});

window.addEventListener("resize", () => session?.resize());
window.addEventListener("visibilitychange", () => {
  if (document.hidden && screen === "playing" && !session?.isTutorialActive()) pauseRun();
});

void init();
